import { Trans, useTranslation } from "react-i18next";

import { WarningBar } from "@clinic/shared/components/WarningBar/WarningBar";
import { clinicRoutes } from "@clinic/shared/data/clinicRoutes";
import { l } from "@clinic/shared/locales/i18n";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { useCertifications } from "@shared/hooks/useCertifications/useCertifications";
import { Link } from "@shared/react-router-dom/react-router-dom";
import { StorePlatform } from "@shared/types/StorePlatform";

import * as styles from "../WarningBars.styles";

interface Props {
  isStoreComplete: boolean;
  storePlatform: StorePlatform;
}

const CertificationWarningBar = ({ isStoreComplete, storePlatform }: Props) => {
  const { t } = useTranslation();
  const isEmerson = storePlatform === StorePlatform.EMERSON;

  const { isCertified, hasSubmittedCertifications, loading } = useCertifications();
  const isFullscriptIncomplete = !isEmerson && !isStoreComplete;
  const isEmersonComplete = isEmerson && isStoreComplete;

  const renderDoesNotHaveCertifications = () => {
    switch (storePlatform) {
      case StorePlatform.FULLSCRIPT:
        return (
          <Trans i18nKey={l.sharedWarningBars.certification.hasNoCertifications}>
            <strong>Certification required.</strong> Orders through your dispensary will not be
            shipped until you upload your credentials and we approve it.
            <strong>
              <Link to={clinicRoutes.accountSettings}>Upload certification &raquo;</Link>
            </strong>
          </Trans>
        );
      case StorePlatform.EMERSON:
        return (
          <Trans i18nKey={l.sharedWarningBars.certification.emerson.hasNoApprovedCertifications}>
            <strong>Certification required.</strong> You cannot place orders until you upload your
            certification and we approve it
            <strong>
              <Link to={emersonRoutes.onboarding}>Upload certification &raquo;</Link>
            </strong>
          </Trans>
        );
    }
  };

  const certificationText = () => {
    switch (storePlatform) {
      case StorePlatform.FULLSCRIPT:
        return (
          <Trans i18nKey={l.sharedWarningBars.certification.hasCertifications}>
            <strong>
              Your certification is pending review by our team. Orders through your dispensary will
              be shipped once we approve your certification.
            </strong>
          </Trans>
        );
      case StorePlatform.EMERSON:
        return t(l.sharedWarningBars.certification.emerson.hasCertifications);
    }
  };

  if (isFullscriptIncomplete || isEmersonComplete || isCertified || loading) {
    return null;
  }

  if (hasSubmittedCertifications) {
    return (
      <WarningBar
        type="primary"
        isFixed={false}
        css={!isEmerson && styles.warningBar}
        data-testid="cert-warning-bar"
        autoHeight={isEmerson}
      >
        {certificationText()}
      </WarningBar>
    );
  }

  return (
    <WarningBar type="danger" isFixed={false} css={!isEmerson && styles.warningBar}>
      {renderDoesNotHaveCertifications()}
    </WarningBar>
  );
};

export { CertificationWarningBar };
